"use client";

import React from "react";
import { MapPin, Quote, Star, ArrowRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const developmentsData = [];
const galleryData = [{
  id: 1,
  image: "https://nsdb.10web.cloud/wp-content/uploads/2026/03/Ecran_Lumiere_03-scaled.jpg?v=d3rmy1gzx2i",
  alt: "Chantier de construction neuve piloté à Lyon"
}, {
  id: 2,
  image: "https://nsdb.10web.cloud/wp-content/uploads/2026/04/Escalier-2-scaled.jpg?v=j0sbdnwpfp",
  alt: "Rénovation d'un appartement de standing avec suivi de chantier"
}, {
  id: 3,
  image: "https://nsdb.10web.cloud/wp-content/uploads/2026/03/NUM0105-scaled.jpg?v=rwmqqjqzz6",
  alt: "Audit énergétique réalisé pour une maison individuelle"
}, {
  id: 4,
  image: "https://nsdb.10web.cloud/wp-content/uploads/2026/04/Facade.jpg?v=hjseagdo8pf",
  alt: "Finitions haut de gamme lors d'une livraison de programme"
}, {
  id: 5,
  image: "https://nsdb.10web.cloud/wp-content/uploads/2026/04/20251128085925-scaled.jpg?v=qr0gmfl76dm",
  alt: "Vue panoramique sur un projet de maîtrise d'œuvre en région"
}, {
  id: 6,
  image: "https://nsdb.10web.cloud/wp-content/uploads/2026/04/IMG_9357-scaled.jpg?v=ep04l5dd0ak",
  alt: "Rénovation d'un appartement contemporain à Lyon"
}, {
  id: 7,
  image: "https://nsdb.10web.cloud/wp-content/uploads/2026/04/P1110292-scaled.jpg?v=0eu8ehbk2cd",
  alt: "Supervision professionnelle d'un chantier de construction"
}, {
  id: 8,
  image: "https://nsdb.10web.cloud/wp-content/uploads/2026/04/IMG_4641-scaled.jpg?v=w34xwilhli",
  alt: "Analyse d'audit thermique pour une maison individuelle"
}, {
  id: 9,
  image: "https://nsdb.10web.cloud/wp-content/uploads/2026/04/1-Entree-8-1-scaled.jpg?v=ju6d99s16yg",
  alt: "Livraison d'un programme résidentiel neuf avec levée de réserves"
}];
const testimonialsData = [{
  id: 1,
  name: "Promoteur Immobilier",
  role: "Maitrise d'Oeuvre et AMO Livraisons",
  text: "J’ai eu la chance d’être accompagnés par ce Moex mais aussi AMO sur plusieurs projets, et je ne peux que les recommander. J’apprécie particulièrement leur grande rigueur et leur exigence, qui sont de réels atouts dans le cadre de la promotion immobilière. Au-delà de leurs compétences techniques, leur implication et leur disponibilité font la différence. [...] C’est un vrai bonheur d’être accompagné par des personnes aussi professionnelles, engagées et humaines, Je les recommande vivement ! Merci encore Nicolas et David !",
  rating: 5
}, {
  id: 2,
  name: "Particulier",
  role: "Maitrise d'oeuvre - Rénovation",
  text: "M.Sicard nous a accompagné de la conception à la fin des travaux. Son implication à été totale, jusqu'à nous accompagner chez l'architecte des BDF pour défendre notre dossier. Le choix des entreprises et la négociation des devis a été optimale. Les imprévus de chantier ont été parfaitement gérés et les délais respectés. Il a suivi le chantier quasiment au quotidien. [...] Nous ne pouvons que recommander vivement cette entreprise à qui nous ferions de nouveau appel, sans hésiter, pour des travaux éventuels.",
  rating: 5
}, {
  id: 3,
  name: "Particulier",
  role: "Audit énergétique",
  text: "Le prestataire NSDB a su parfaitement comprendre la mission qui consistait en un audit énergétique d'une maison individuelle qui permette d'en déduire les travaux à réaliser [...] avec un objectif de performance énergétique ambitieux. Je porte une excellente appréciation sans aucune observation, sur le déroulement de la mission depuis le 1er contact jusqu'à la facturation, ainsi que sur le soin avec lequel la réflexion technique a été conduite et le rapport établi.",
  rating: 5
}];
export default function Developments() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="203394" id="territoires" className="bg-background text-foreground overflow-hidden ![padding:64px_0px_128px_0px]">
      <div ref={ref} className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          <Badge variant="outline" className="border-primary/20 text-primary uppercase tracking-wider font-mono">NSDB &amp; VOUS</Badge>
          <h2 className="text-3xl md:text-5xl font-default font-bold tracking-tight max-w-3xl">Territoires &amp; Réalisations</h2>
          <p className="text-muted-foreground text-lg max-w-2xl font-sans">Découvrez notre zone d'intervention géographique, quelques exemples de réalisations et les derniers avis de nos clients sur Google.</p>
        </div>

        <Tabs defaultValue="developments" className="w-full flex flex-col items-center">
          <TabsList className="mb-12 bg-muted/80 p-1 rounded-full border border-border">
            <TabsTrigger value="developments" className="rounded-full px-6 py-2.5 font-sans text-base">
              Territoires
            </TabsTrigger>
            <TabsTrigger value="gallery" className="rounded-full px-6 py-2.5 font-sans text-base">
              Réalisations
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="rounded-full px-6 py-2.5 font-sans text-base">
              Avis Clients
            </TabsTrigger>
          </TabsList>

          <div className="w-full">
            {}
            <TabsContent value="developments" className="mt-0 outline-none animate-in fade-in-50 duration-500">
              <div className="grid grid-cols-1 gap-8 bg-cover bg-center bg-no-repeat !bg-[url(https://nsdb.10web.cloud/wp-content/uploads/2026/04/Carte-geographique.jpg)] !h-[1000px] !w-[1000px] mx-auto !p-0 md:grid-cols-2">
                {developmentsData.map((dev, i) => <Card key={dev.id} data-index={i} className="bg-card border-border/50 overflow-hidden group hover:border-primary/30 transition-colors duration-300 rounded-xl shadow-sm">
                    <div className="relative overflow-hidden">
                      
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-background/90 text-foreground backdrop-blur-sm border-none shadow-sm font-sans">
                          {dev.status}
                        </Badge>
                      </div>
                    </div>
                    
                    <CardContent>
                      
                    </CardContent>
                    <CardFooter>
                      
                    </CardFooter>
                  </Card>)}
              </div>
            </TabsContent>

            {}
            <TabsContent value="gallery" className="mt-0 outline-none animate-in fade-in-50 duration-500">
              <div className="relative px-4 md:px-12">
                <Carousel opts={{
                align: "start",
                loop: true
              }} className="w-full">
                  <CarouselContent className="-ml-4 md:-ml-6">
                    {galleryData.map((item, i) => <CarouselItem key={item.id} data-index={i} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-2/3">
                        <div className="overflow-hidden rounded-xl border border-border/50 bg-card">
                          <AspectRatio ratio={16 / 10}>
                            <img src={item.image} alt={item.alt} className="object-cover w-full h-full hover:scale-105 transition-transform duration-700" />
                          </AspectRatio>
                        </div>
                      </CarouselItem>)}
                  </CarouselContent>
                  <CarouselPrevious className="hidden md:flex -left-4 bg-background/90 backdrop-blur-sm border-border/50 hover:bg-primary hover:text-primary-foreground" />
                  <CarouselNext className="hidden md:flex -right-4 bg-background/90 backdrop-blur-sm border-border/50 hover:bg-primary hover:text-primary-foreground" />
                </Carousel>
              </div>
            </TabsContent>

            {}
            <TabsContent value="testimonials" className="mt-0 outline-none animate-in fade-in-50 duration-500">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonialsData.map((testimonial, i) => <Card key={testimonial.id} data-index={i} className="bg-card border-border/50 flex flex-col h-full rounded-xl shadow-sm">
                    <CardHeader className="pb-4">
                      <img src="https://nsdb.10web.cloud/wp-content/uploads/2026/04/2000px-Google_G_Logo.svg_.jpg?v=hbpv4sux22c" className="h-7 mb-4" alt="Google" />
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                      </div>
                      <Quote className="w-10 h-10 text-primary/10 mb-2" />
                      <p className="text-foreground/90 font-sans leading-relaxed flex-grow italic">
                        "{testimonial.text}"
                      </p>
                    </CardHeader>
                    <CardFooter className="flex items-center [.border-t]:pt-6 mt-auto border-t border-border/10 !justify-end ![padding:0px_24px_0px_24px]">
                      <div className="![margin:0px_0px_0px_150px]">
                        <CardTitle className="font-semibold text-base font-default !text-right !w-[300px]">{testimonial.name}</CardTitle>
                        <CardDescription className="text-sm font-sans text-primary/80 mt-1 !text-right">{testimonial.role}</CardDescription>
                      </div>
                    </CardFooter>
                  </Card>)}
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </section>;
}